import { useRef, useMemo } from 'react';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';
import { FoodOrb } from '@/types/game';

interface FoodProps {
  food: FoodOrb[];
}

export const Food = ({ food }: FoodProps) => {
  const instancedMeshRef = useRef<THREE.InstancedMesh>(null);
  const timeRef = useRef(0);
  
  const tempObject = useMemo(() => new THREE.Object3D(), []);
  const tempColor = useMemo(() => new THREE.Color(), []);

  // Create color array for instanced rendering
  const colorArray = useMemo(() => {
    const colors = new Float32Array(food.length * 3);
    food.forEach((orb, i) => {
      tempColor.set(orb.color);
      colors[i * 3] = tempColor.r;
      colors[i * 3 + 1] = tempColor.g;
      colors[i * 3 + 2] = tempColor.b;
    });
    return colors;
  }, [food, tempColor]);

  useFrame((state, delta) => {
    if (!instancedMeshRef.current) return;
    
    timeRef.current += delta;
    
    food.forEach((orb, i) => {
      // Gentle floating animation
      const floatOffset = Math.sin(timeRef.current * 2 + i * 0.5) * 0.3;
      const pulseScale = 1 + Math.sin(timeRef.current * 3 + i * 0.3) * 0.1;
      
      tempObject.position.set(
        orb.position.x,
        orb.position.y + floatOffset,
        orb.position.z
      );
      tempObject.scale.setScalar(orb.radius * pulseScale);
      tempObject.updateMatrix();
      
      instancedMeshRef.current!.setMatrixAt(i, tempObject.matrix);
    });
    
    instancedMeshRef.current.instanceMatrix.needsUpdate = true;
  });

  return (
    <instancedMesh
      ref={instancedMeshRef}
      args={[undefined, undefined, food.length]}
      frustumCulled={false}
    >
      <sphereGeometry args={[1, 8, 8]}>
        <instancedBufferAttribute
          attach="attributes-color"
          args={[colorArray, 3]}
        />
      </sphereGeometry>
      <meshStandardMaterial
        vertexColors
        emissive="white"
        emissiveIntensity={0.3}
        transparent
        opacity={0.9}
      />
    </instancedMesh>
  );
};
